/**
 * The free-body diagram, which is what this engine is really for.
 *
 * The multipliers are the answer, but they are not the EXPLANATION. A student
 * asked for the acceleration of a weight does not solve a KKT system: they
 * draw the weight on its own, mark every force acting on it, add them up and
 * divide by the mass. This produces exactly that artefact — every force named,
 * directed, and attributed to the link it came from.
 *
 * Three things it buys, and the third is why it is a first-class output rather
 * than a debugging aid:
 *
 *   the bench can DRAW it, which is the single most useful picture in school
 *   mechanics and the engine has it by construction;
 *   a reader who disagrees with a number can find out where;
 *   and `ΣF = ma` on every body, on every arrangement, is a self-check worth
 *   more than any hand-written expected value — a solver that is wrong in an
 *   interesting way cannot satisfy it.
 */
import { defOf } from './registry';
import { add, dot, scale } from './vec';
import { Body, Constraint, PortRef, Rope, Vec2, World } from './types';
import { GRAM, Solution } from './solve';
import { forceLine } from './constraints';
import { anyPinned, bodiesOf, bodyIndex, bondsOf, isFree, partById, portLabel, portsOf, released, Segment, segmentsOf, spanId, tensionOf, normalNames } from './world';

export interface BodyForce {
    kind: 'weight' | 'tension' | 'normal' | 'friction' | 'applied';
    /** What the reader is shown: "mg", "T₁", "N from the floor". */
    label: string;
    /** Newtons, in bench coordinates. */
    vector: Vec2;
    /** The link or contact it came from, so the drawing can highlight both. */
    sourceId?: string;
    /** Where on the body it acts, for an arrow that starts in the right place. */
    at?: Vec2;
    /**
     * ACTS, but by an amount nothing here decides — `vector` is then a
     * direction of unit length and not a number of newtons.
     *
     * A free-body diagram is a statement about WHICH forces act, and only
     * secondly about how big they are. A rope tied to a weight pulls on it
     * whether or not the arrangement pins the value down, and leaving the arrow
     * out because the number is unknown draws a different body — one with
     * nothing attached to it.
     */
    unknown?: boolean;
}

export interface FreeBody {
    bodyId: string;
    /** The part the reader thinks of this body as. */
    name: string;
    /**
     * Its part kind, so a sketch can draw it as the thing it is.
     *
     * Absent for a body that is several parts bolted together: an assembly
     * looks like nothing in particular, and a plain outline says so honestly.
     */
    kind?: string;
    mass: number;
    forces: BodyForce[];
    /** Σ forces. */
    net: Vec2;
    /**
     * What whatever it is bolted to has to supply — set only for a body that
     * cannot move.
     *
     * A fixed pulley's diagram is two tensions pulling down and nothing at all
     * pushing back, which reads as a pulley accelerating into the floor. The
     * missing arrow is the ceiling's, and it is exactly `−net`. Kept OUT of
     * `net` on purpose: `net` is what the body's own forces come to, which is
     * the number `carried` reports as the load on the hook, and folding the
     * reaction in would make every fixed part carry nothing.
     */
    hold?: Vec2;
    /** Whether that hold is a PIN — temporary, and gone the moment the machine
     *  is started — or a fixture, which is part of what the machine is. Two
     *  different sentences to a reader, and the same arithmetic either way. */
    pinned?: boolean;
    /** Resolved along the body's axes — what actually drives it. */
    alongAxes: number[];
    /** net ⁄ mass along each axis: the line of working the reader would write. */
    acceleration: number[];
    /**
     * What is left over in a direction this body cannot travel — set only when
     * it is FREE, and only when it is not zero.
     *
     * A hanging body has one axis, straight down, so a rope that does not hang
     * vertically pulls it sideways with nothing to balance it. A real load
     * would swing until the rope came under it; this bench holds it on its one
     * line and works out the vertical answer, which is a perfectly good answer
     * to a different question.
     *
     * `net` keeps the leftover and `alongAxes` does not, which is why
     * `checkNewton` passes on an arrangement like that: ΣF = ma is true along
     * every axis the body HAS. The engine has to say the rest out loud rather
     * than let the silence read as agreement — see §1 of the target.
     */
    sideways?: Vec2;
}

/** How a rope's tension pulls the part at one end of one of its runs. */
function ropePull(dirAB: Vec2, atA: boolean, T: number): Vec2 {
    // A rope pulls each end towards the other.
    return scale(dirAB, atA ? T : -T);
}

/**
 * Where a rope force is shown ACTING on a part.
 *
 * On a wheel: its CENTRE. A pulley has no size in the model — §5.2 — so both
 * legs of a rope over it act through one point, and drawing them from the rims
 * says the opposite: two forces a diameter apart, which is a picture of a
 * couple and would raise a question about torque that the ideal model does not
 * have. The rim is where the rope TOUCHES, and that is a fact about the
 * drawing; the line of action passes through the middle.
 *
 * On anything else: where the rope is actually tied, because a weight really is
 * held by the hook on its top face and that is worth seeing.
 */
function actsAt(w: World, ref: PortRef, touch: Vec2): Vec2 {
    const part = partById(w, ref.partId);
    if (!part) return touch;
    const port = portsOf(w, part).find(p => p.id === ref.portId);
    return port?.role === 'guide' ? { ...part.at } : touch;
}


export function freeBodies(w: World, bodies: Body[], constraints: Constraint[], sol: Solution): FreeBody[] {
    const byPart = bodyIndex(bodies);
    /* Which parts a pin is actually holding up.
     *
     * A part placed loose arrives pinned and may later be bolted to a hook, at
     * which point the hook is what stops it moving and the pin is doing
     * nothing at all. Saying "the pin supplies 39.2 N" of a pulley bolted to a
     * beam names the wrong thing entirely — so the question is not "is it
     * pinned" but "would it move if the pins came out". */
    const wouldMove = new Set<string>();
    if (anyPinned(w)) {
        bodiesOf(released(w)).filter(isFree).forEach(b => b.partIds.forEach(id => wouldMove.add(id)));
    }
    const segs = segmentsOf(w);
    const byId = new Map(constraints.map(c => [c.id, c]));

    return bodies.map(body => {
        const forces: BodyForce[] = [];

        if (body.mass > 0) {
            forces.push({
                kind: 'weight',
                label: 'mg',
                vector: { x: 0, y: -body.mass * w.gravity },
            });
        }

        // ── every rope run touching this body ────────────────────────────
        segs.forEach(seg => {
            /* Each run pulls with the tension of ITS span. The same number for
             * every run of an ideal rope, and not the same once something along
             * it can grip. */
            const T = tensionOf(sol.force, seg);
            const rowId = spanId(seg.ropeId, seg.span);
            /* Undecided is not the same as absent. A rope every one of whose
             * parts is held has a tension nothing works out — but it is still
             * tied on, and still pulling. Drawn at unit length, so the diagram
             * says which way without claiming how hard. */
            const vague = T === undefined && !!sol.undecided?.includes(rowId);
            if (!vague && (T === undefined || Math.abs(T) < 1e-9)) return;
            const size = vague ? 1 : (T as number);
            const c = byId.get(rowId);
            const label = c?.label ?? 'T';
            /* Named by WHERE it acts as well as by which rope. A fixed pulley
             * has the same rope pulling on it twice, and two arrows both
             * labelled "the rope" tell a reader nothing about which leg is
             * which — which is the whole question when the two are unequal. */
            if (byPart.get(seg.a.partId)?.id === body.id) {
                forces.push({ kind: 'tension', label: `${label} at ${portLabel(w, seg.a)}`, vector: ropePull(seg.dir, true, size), sourceId: seg.ropeId, at: actsAt(w, seg.a, seg.pa), ...(vague ? { unknown: true } : {}) });
            }
            if (byPart.get(seg.b.partId)?.id === body.id) {
                forces.push({ kind: 'tension', label: `${label} at ${portLabel(w, seg.b)}`, vector: ropePull(seg.dir, false, size), sourceId: seg.ropeId, at: actsAt(w, seg.b, seg.pb), ...(vague ? { unknown: true } : {}) });
            }
        });

        /* ── contacts, separations and friction ──────────────────────────
         *
         * A surface's normal force and its drag arrive here by exactly the
         * same road as a rope's tension: they are multipliers, so they are
         * drawn, named and added up with everything else — and `checkNewton`
         * then holds them to `ΣF = ma` rather than taking them on trust. That
         * is the return on giving a block on a table a second coordinate. */
        constraints.forEach(c => {
            const N = sol.force[c.id];
            if (N === undefined || Math.abs(N) < 1e-9) return;
            if (c.kind !== 'contact' && c.kind !== 'separation' && c.kind !== 'friction') return;
            /* ONE arrow per row, in the direction `forceLine` says — not one
             * per axis from the row coefficients. A separation acts along the
             * RUN, and reading its direction off a hanging body's single
             * vertical axis threw the sideways half away here while the statics
             * had already been taught not to. */
            const dir = forceLine(w, c, body, byPart);
            if (Math.hypot(dir.x, dir.y) < 1e-12) return;
            const rub = c.kind === 'friction';
            /* A surface's own push on what rests on it is THE normal force, and
               is called what a textbook calls it — N, or N₁, N₂. Its ends, the
               floor and the rest keep saying where they come from. */
            const normal = !rub && c.sourceId && c.id === `rest:${c.sourceId}` ? normalNames(w).get(c.sourceId) : undefined;
            forces.push({
                kind: rub ? 'friction' : 'normal',
                label: normal ?? (c.label
                    ? (rub ? `friction from ${c.label}` : `N from ${c.label}`)
                    : (rub ? 'f' : 'N')),
                vector: scale(dir, N),
                sourceId: c.sourceId,
            });
        });

        // ── whatever the parts themselves apply ──────────────────────────
        body.partIds.forEach(id => {
            const part = partById(w, id);
            if (!part) return;
            (defOf(part).forces?.(part as never, w) ?? []).forEach(f => {
                forces.push({ kind: 'applied', label: f.label, vector: f.vector, sourceId: id, at: part.at });
            });
        });

        /* Only what is KNOWN adds up. An unknown force has a direction and no
         * size, so folding it into the sum would be inventing one. */
        const vagueness = forces.some(f => f.unknown);
        const net = forces.filter(f => !f.unknown).reduce((s, f) => add(s, f.vector), { x: 0, y: 0 } as Vec2);
        const alongAxes = body.axes.map(a => dot(net, a));
        /* Whatever the axes do not span. Zero for a block on a face, whose two
         * coordinates span the plane; zero for anything hanging on a vertical
         * rope; and equal to the horizontal pull for anything that is not. */
        const spanned = body.axes.reduce(
            (s, a, i) => add(s, scale(a, alongAxes[i])), { x: 0, y: 0 } as Vec2,
        );
        const leftover: Vec2 = { x: net.x - spanned.x, y: net.y - spanned.y };
        const adrift = isFree(body) && Math.hypot(leftover.x, leftover.y) > 1e-6;
        const acceleration = isFree(body) && body.mass > 0
            ? alongAxes.map(f => f / body.mass)
            : body.axes.map(() => 0);

        /* Parts bolted together are ONE thing and move as one, so the row in
         * the readouts is about all of them — "M₃ + P₁", not whichever of the
         * two happened to be first in the list. The weight leads, because that
         * is what a reader thinks of a loaded block as. */
        const mine = body.partIds.map(i => partById(w, i)).filter((p): p is NonNullable<typeof p> => !!p);
        const ordered = [...mine.filter(p => p.kind === 'weight'), ...mine.filter(p => p.kind !== 'weight')];
        const name = ordered.length
            ? ordered.map(p => defOf(p).name(p as never, w)).join(' + ')
            : body.id;

        return {
            bodyId: body.id,
            name,
            // One part: draw it as itself. Several bolted together: an
            // assembly is not shaped like any of them.
            ...(mine.length === 1 ? { kind: mine[0].kind } : {}),
            mass: body.mass,
            forces,
            net,
            ...(isFree(body) ? {} : {
                /* The holding is only a NUMBER when everything else on the
                 * diagram is. A pin takes up whatever the rest do not, so a
                 * rope on the same body with no value leaves the pin with no
                 * value either — and "the pin supplies 49.0 N" beside a tension
                 * shown as `?` is the bench asserting the rope carries nothing,
                 * which is precisely what is not known. */
                ...(vagueness ? {} : { hold: { x: -net.x, y: -net.y } }),
                pinned: body.partIds.some(id => partById(w, id)?.pinned)
                    && body.partIds.some(id => wouldMove.has(id)),
            }),
            ...(adrift ? { sideways: leftover } : {}),
            alongAxes,
            acceleration,
        };
    });
}

/**
 * The engine checking itself: does every body satisfy ΣF = ma?
 *
 * Run on every arrangement in the tests. A solve that passes this is not
 * necessarily the arrangement the student meant, but it IS Newton's second law
 * — and no solver that is subtly wrong survives it.
 */
/**
 * The prefix on the one failure that is the MODEL's and not the solve's.
 *
 * A body with no mass left holding a force is not a rounding error and not a
 * bad active set: it is the dynamics having no coordinate for the direction
 * the force points in. Every hanging body travels along one vertical axis, so
 * a rope pulling it sideways has nowhere to go and is dropped — silently,
 * until this check existed. Tagged so callers can tell "this run is wrong"
 * from "this bench is one the runner cannot answer yet", because they are
 * different sentences and only the second is fixed by giving parts a second
 * freedom.
 */
export const SIDEWAYS = 'cannot answer sideways —';

export function checkNewton(bodies: Body[], fbs: FreeBody[], sol: Solution, tol = 1e-6): string[] {
    const out: string[] = [];
    fbs.forEach(fb => {
        const body = bodies.find(b => b.id === fb.bodyId) as Body;
        if (!isFree(body)) return;
        const a = sol.acceleration[fb.bodyId] ?? [];
        /* What the solver LENT this body, if it has no mass of its own: a
         * massless coordinate is given `GRAM` of inertia to steady an
         * otherwise ill-conditioned system (see `solve.ts`), so a weightless
         * part accelerating at `a` really does carry `GRAM · a`. Expecting
         * exactly zero here would report the regularisation as a fault on
         * every phase of every run. */
        const lent = body.mass > 1e-9 ? 0 : GRAM;
        fb.alongAxes.forEach((F, i) => {
            const expected = (body.mass + lent) * (a[i] ?? 0);
            if (Math.abs(F - expected) > tol + tol * Math.abs(expected)) {
                out.push(`${fb.name} axis ${i}: ΣF = ${F.toFixed(6)} but ma = ${expected.toFixed(6)}`);
            }
        });

        /* And ACROSS the axes, which is where the real failures hide.
         *
         * The loop above asks the question along each of the body's own
         * freedoms, so a body with one vertical axis is never asked about
         * sideways at all — and a run carrying 2.9 N sideways on a MASSLESS
         * pulley passed this check with no residuals at all. `ΣF = 0` on a
         * weightless body is a law in every direction, not along the one
         * coordinate the model happens to give it, and a force with nowhere to
         * go is not a force that can be ignored: it is the model being unable
         * to express the motion.
         *
         * Asked only of bodies with no mass, because that is where it is a
         * law. A body WITH mass and a leftover across its axes is being held
         * by whatever it travels along — a block on a table pressed into the
         * table — which is a normal force and a real answer. */
        if (body.mass > 1e-9) return;
        const leftover = fb.forces.reduce((v, f) => add(v, f.vector), { x: 0, y: 0 } as Vec2);
        /* Allowing for the gram of inertia the solver lends a massless
         * coordinate (`GRAM` in `solve.ts`). It is lent for a reason and it is
         * not free: a weightless part accelerating at `a` then carries
         * `GRAM · a` of force, which is 6 mN on a hand paying out rope at
         * 6 m/s² — far below anything a reader could read, and far above the
         * tolerance this check would otherwise apply. */
        const speeding = Math.hypot(...(a.length ? a : [0]));
        if (Math.hypot(leftover.x, leftover.y) > tol * 1e3 + 2 * GRAM * speeding) {
            out.push(`${SIDEWAYS} ${fb.name}: ΣF = (${leftover.x.toFixed(6)}, ${leftover.y.toFixed(6)})`
                + ' on a body with no mass, which can carry no force in any direction');
        }
    });
    return out;
}

/**
 * The free-body diagrams of a rope — one per RUN, not one for the whole rope.
 *
 * A rope is not a body. Drawn as one, with the end pulls and every wheel's
 * push on a single shapeless outline, it says only that the whole thing is in
 * balance — which is true of any rope and explains nothing. Worse, it invites
 * the reading that a rope bent over a wheel is one straight object being
 * pushed sideways.
 *
 * Cut it at each wheel instead. What is left is a set of STRAIGHT massless
 * pieces, and each one is a real free body with exactly two forces on it:
 * whatever is at either end, pulling it outward along its own line. With no
 * mass those two must cancel, so they are equal and opposite — which is the
 * whole of "the tension is the same all along a run", derived rather than
 * asserted, once per run, where the reader can see it.
 *
 * The two runs either side of a wheel share a tension for a different reason —
 * the wheel is massless and turns freely, so it can take no torque — and that
 * argument belongs on the WHEEL's diagram, which is where it is drawn.
 */
/** The name of the PART a run ends at — never the port's, which is geometry. */
function nameAt(w: World, ref: PortRef): string {
    const part = partById(w, ref.partId);
    return part ? defOf(part).name(part as never, w) : ref.partId;
}

export function ropeDiagrams(w: World, rope: Rope, force: Record<string, number>): FreeBody[] {
    const segs = segmentsOf(w).filter(s => s.ropeId === rope.id);
    const out: FreeBody[] = [];
    segs.forEach((seg, i) => {
        const T = force[spanId(seg.ropeId, seg.span)];
        if (T === undefined) return;
        /* At an interior end the pull comes from the REST OF THE ROPE, carried
         * round the wheel — not from the wheel, which pushes at a right angle
         * to this run and does not touch it along its length. Saying "pull from
         * P₁" there would name the wrong body for the force. */
        const held = (k: number, stop: Segment['a']): string => (
            k === 0 || k === segs.length
                ? `pull from ${nameAt(w, stop)}`
                : `pull from the rope, round ${nameAt(w, stop)}`
        );
        const forces: BodyForce[] = [
            { kind: 'tension', label: held(i, seg.a), vector: scale(seg.dir, -T), at: seg.pa },
            { kind: 'tension', label: held(i + 1, seg.b), vector: scale(seg.dir, T), at: seg.pb },
        ];
        out.push({
            bodyId: spanId(seg.ropeId, seg.span),
            name: `${nameAt(w, seg.a)} → ${nameAt(w, seg.b)}`,
            kind: 'rope',
            mass: 0,
            forces,
            net: forces.reduce((a, f) => add(a, f.vector), { x: 0, y: 0 } as Vec2),
            alongAxes: [],
            acceleration: [],
        });
    });
    return out;
}

/**
 * The free-body diagram of ONE PART inside a bolted assembly.
 *
 * A bond is identity, not a force: two bolted parts are one body, and the
 * solver never computes what passes through the bolt because it never needs
 * to. But a reader looking at a pulley bolted to a hook is owed the answer to
 * "what holds the pulley up", and "it is the same object as the hook" is not
 * that answer. The bolt carries 98 N and that is a number worth seeing.
 *
 * It is recoverable exactly, without the solver knowing anything new. Cut the
 * bolt: the far side is then its own free body, and Newton fixes what must be
 * passing through the cut —
 *
 *     f = Σ(external forces on the far side) − (mass of the far side)·a
 *
 * by the third law that is the force the far side exerts on this part. For a
 * chain of bolts the cut is still unique as long as the bolts form a TREE,
 * which they do unless somebody has bolted a loop; a loop shares the load in a
 * way nothing here can decide, and is reported rather than guessed.
 */
export function partDiagram(
    w: World, partId: string, bodies: Body[], constraints: Constraint[], sol: Solution,
): FreeBody | null {
    const part = partById(w, partId);
    const body = bodies.find(b => b.partIds.includes(partId));
    if (!part || !body) return null;

    /* Which part of a held assembly the holding acts ON: whatever cannot move
     * on its own — an anchor, a surface — or else whatever is pinned. */
    const fixedOne = body.partIds.find(id => {
        const p = partById(w, id);
        return p && defOf(p).freedom(p as never, w).axes.length === 0;
    }) ?? body.partIds.find(id => partById(w, id)?.pinned) ?? body.partIds[0];

    const segs = segmentsOf(w);
    const byId = new Map(constraints.map(c => [c.id, c]));
    const byPart = bodyIndex(bodies);
    const accel = body.axes.reduce(
        (s, axis, i) => add(s, scale(axis, (sol.acceleration[body.id] ?? [])[i] ?? 0)),
        { x: 0, y: 0 } as Vec2,
    );

    /** Everything acting on one part from OUTSIDE the assembly. */
    const outside = (id: string): BodyForce[] => {
        const p = partById(w, id);
        if (!p) return [];
        const out: BodyForce[] = [];
        const m = defOf(p).mass(p as never);
        if (m > 0) out.push({ kind: 'weight', label: 'mg', vector: { x: 0, y: -m * w.gravity } });
        segs.forEach(seg => {
            const T = tensionOf(sol.force, seg);
            if (T === undefined || Math.abs(T) < 1e-9) return;
            const label = byId.get(spanId(seg.ropeId, seg.span))?.label ?? 'T';
            if (seg.a.partId === id) out.push({ kind: 'tension', label: `${label} at ${portLabel(w, seg.a)}`, vector: ropePull(seg.dir, true, T), sourceId: seg.ropeId, at: actsAt(w, seg.a, seg.pa) });
            if (seg.b.partId === id) out.push({ kind: 'tension', label: `${label} at ${portLabel(w, seg.b)}`, vector: ropePull(seg.dir, false, T), sourceId: seg.ropeId, at: actsAt(w, seg.b, seg.pb) });
        });
        constraints.forEach(c => {
            const N = sol.force[c.id];
            if (N === undefined || Math.abs(N) < 1e-9 || c.sourceId !== id) return;
            if (c.kind !== 'contact' && c.kind !== 'friction') return;
            const dir = forceLine(w, c, body, byPart);
            if (Math.hypot(dir.x, dir.y) < 1e-12) return;
            const rub = c.kind === 'friction';
            const normal = !rub && c.id === `rest:${id}` ? normalNames(w).get(id) : undefined;
            out.push({
                kind: rub ? 'friction' : 'normal',
                label: normal ?? (c.label ? (rub ? `friction from ${c.label}` : `N from ${c.label}`) : (rub ? 'f' : 'N')),
                vector: scale(dir, N),
                sourceId: c.sourceId,
            });
        });
        (defOf(p).forces?.(p as never, w) ?? []).forEach(f =>
            out.push({ kind: 'applied', label: f.label, vector: f.vector, sourceId: id, at: p.at }));
        return out;
    };

    const forces = outside(partId);

    // ── what comes through each bolt ────────────────────────────────────
    const inside = new Set(body.partIds);
    const links = bondsOf(w).filter(b => inside.has(b.hangs.partId) && inside.has(b.holds.partId));
    const nextTo = (id: string): { other: string }[] => links
        .filter(b => b.hangs.partId === id || b.holds.partId === id)
        .map(b => ({ other: b.hangs.partId === id ? b.holds.partId : b.hangs.partId }));

    let looped = false;
    nextTo(partId).forEach(({ other }) => {
        // Everything reachable from `other` without coming back through us.
        const far = new Set<string>([other]);
        const queue = [other];
        while (queue.length) {
            const here = queue.shift() as string;
            nextTo(here).forEach(n => {
                if (n.other === partId || far.has(n.other)) return;
                far.add(n.other); queue.push(n.other);
            });
        }
        if (far.has(partId)) { looped = true; return; }
        let f: Vec2 = { x: 0, y: 0 };
        let mass = 0;
        far.forEach(id => {
            outside(id).forEach(x => { f = add(f, x.vector); });
            const p = partById(w, id);
            if (p) mass += defOf(p).mass(p as never);
        });
        // The holding, if it lands on the far side.
        if (!isFree(body) && far.has(fixedOne)) {
            const whole = body.partIds.flatMap(outside).reduce((s, x) => add(s, x.vector), { x: 0, y: 0 } as Vec2);
            f = add(f, { x: -whole.x, y: -whole.y });
        }
        const via = partById(w, other);
        forces.push({
            kind: 'applied',
            label: `the bolt to ${via ? defOf(via).name(via as never, w) : other}`,
            vector: { x: f.x - mass * accel.x, y: f.y - mass * accel.y },
        });
    });

    // The holding, when it lands on THIS part.
    let hold: Vec2 | undefined;
    if (!isFree(body) && partId === fixedOne) {
        const whole = body.partIds.flatMap(outside).reduce((s, x) => add(s, x.vector), { x: 0, y: 0 } as Vec2);
        hold = { x: -whole.x, y: -whole.y };
    }

    const net = forces.reduce((s, f) => add(s, f.vector), { x: 0, y: 0 } as Vec2);
    const mine = defOf(part).mass(part as never);
    return {
        bodyId: partId,
        name: defOf(part).name(part as never, w) + (looped ? ' (bolted in a loop — the share is undecidable)' : ''),
        kind: part.kind,
        mass: mine,
        forces,
        net,
        ...(hold ? { hold, pinned: !!partById(w, fixedOne)?.pinned } : {}),
        alongAxes: body.axes.map(a => dot(net, a)),
        acceleration: body.axes.map((_, i) => (sol.acceleration[body.id] ?? [])[i] ?? 0),
    };
}

